import {
    CloudOffOutlined as CloudOffOutlinedIcon,
    FilterAltOffOutlined as FilterAltOffOutlinedIcon,
    InboxOutlined as InboxOutlinedIcon,
    Refresh as RefreshIcon,
} from 'components/icons';
import { usePostHog } from '@posthog/react';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import AISparkleIcon from 'assets/images/icons/AISparkle.svg?react';
import { dispatch } from 'store';
import { setFloatingAiVisibility } from 'store/reducers/aiChat';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

/**
 * Size tier, picked from the space the state has to fill — NOT from what the surface is called.
 *  - `page`   full page or detail tab (≥400px): 48px icon, 16px title, CTA, AI/support link row.
 *  - `panel`  card, widget, drawer (150–400px): 32px icon, 14px title, CTA, no link row.
 *  - `inline` tight slot (<150px): one 13px line plus an optional extraSmall button. No icon —
 *             the ceiling is 48px without the button and 88px with it (a single `colSpan`
 *             table row is ~48px).
 */
export type EmptyStateSize = 'page' | 'panel' | 'inline';

/**
 * Which of the three no-rows outcomes this is. `empty` and `filtered` look the same but say
 * different things, and `error` must be distinguishable from both.
 */
export type EmptyStateVariant = 'empty' | 'filtered' | 'error';

type MessageValues = Record<string, React.ReactNode>;

type Props = {
    size?: EmptyStateSize;
    variant?: EmptyStateVariant;
    /** Glyph from `components/icons`. Defaults per variant; ignored at the `inline` tier, which has no icon. */
    icon?: React.ElementType;
    /** Per-surface title key. Falls back to the variant's generic key. */
    titleId?: string;
    titleValues?: MessageValues;
    /** Per-surface body key. Falls back to the variant's generic key; `null` suppresses the body. */
    bodyId?: string | null;
    bodyValues?: MessageValues;
    /**
     * The surface's own call to action, for the `empty` variant. Pass a `PermissionButton` so it
     * disappears for users who cannot perform it — never render a CTA that would be refused.
     * `filtered` and `error` supply their own CTA from the callbacks below.
     */
    action?: React.ReactNode;
    /** Renders the "Clear filters" CTA. Only read on the `filtered` variant. */
    onClearFilters?: () => void;
    /** Renders the "Retry" CTA. Only read on the `error` variant. */
    onRetry?: () => void;
    /**
     * Copy for the `error` variant that names what failed, when the generic "Couldn't load this list"
     * would misdescribe it (a page that cannot open rather than a list that is empty).
     */
    errorTitleId?: string;
    errorBodyId?: string;
    /** The search a `filtered` list came up empty on, which its body then names. */
    searchText?: string;
    /** Analytics label for this surface. Defaults to the resolved title key. */
    surface?: string;
    className?: string;
};

const GENERIC_TITLE_ID: Record<EmptyStateVariant, string> = {
    empty: 'empty-state.generic.title',
    filtered: 'empty-state.filtered.title',
    error: 'empty-state.error.title',
};

/**
 * The key of the title a state shows, for the host that announces it. A state mounts together with its text,
 * which a live region must not: the host keeps a `role="status"` of its own mounted from the start and feeds
 * it these words as the state appears.
 */
export function emptyStateTitleId(variant: EmptyStateVariant, titleId?: string, errorTitleId?: string): string {
    const surfaceTitleId = variant === 'empty' ? titleId : variant === 'error' ? errorTitleId : undefined;
    return surfaceTitleId ?? GENERIC_TITLE_ID[variant];
}

const GENERIC_BODY_ID: Record<EmptyStateVariant, string | null> = {
    // A tenant-empty list says what will land here in words only the surface knows, so there is no
    // useful generic sentence — the generic title stands alone until a surface passes `bodyId`.
    empty: null,
    filtered: 'empty-state.filtered.body',
    error: 'empty-state.error.body',
};

const DEFAULT_ICON: Record<EmptyStateVariant, React.ElementType> = {
    empty: InboxOutlinedIcon,
    filtered: FilterAltOffOutlinedIcon,
    error: CloudOffOutlinedIcon,
};

/** The row is a pair of text links, whether the link opens a panel or a mail client. */
const LINK_CLASS = 'text-link new:text-meta text-[12.5px] font-medium no-underline hover:underline';

/**
 * The AI / support link row under a page-tier state. Its own component so the AI feature flag and
 * permission hooks are only ever called on the tier that renders it.
 */
const EmptyStateLinks = () => {
    const isAiEnabled = useIsFeatureEnabled(FeatureFlag.AI_CHAT_ENABLED);
    const hasAiPermission = useHasPermissions(['READ.CRESPECT_AI']);
    const showAskAi = isAiEnabled && hasAiPermission;

    return (
        <div className="border-divider new:text-meta mt-7 flex w-full max-w-[460px] items-center justify-center gap-[18px] border-t pt-[22px] text-[12.5px]">
            {showAskAi ? (
                <>
                    <button
                        type="button"
                        className={cn(LINK_CLASS, 'inline-flex cursor-pointer items-center gap-1.5')}
                        onClick={() => dispatch(setFloatingAiVisibility('open'))}
                    >
                        <AISparkleIcon width={14} height={14} aria-hidden />
                        <FormattedMessage id="empty-state.action.ask-ai" />
                    </button>
                    <span className="text-fg-secondary opacity-40" aria-hidden>
                        ·
                    </span>
                </>
            ) : null}
            <a href="mailto:support@crespect.com" className={LINK_CLASS}>
                <FormattedMessage id="empty-state.action.contact-support" />
            </a>
        </div>
    );
};

/**
 * The one designed empty state, for every list, table, card and drawer that can run out of rows.
 *
 * Callers own only the copy and the CTA; the tier owns every dimension. `NoData` is a thin wrapper
 * over the `inline` tier, so its call sites get the design without changing.
 */
const EmptyState: React.FC<Props> = ({
    size = 'page',
    variant = 'empty',
    icon,
    titleId,
    titleValues,
    bodyId,
    bodyValues,
    action,
    onClearFilters,
    onRetry,
    errorTitleId,
    errorBodyId,
    searchText,
    surface,
    className,
}) => {
    const posthog = usePostHog();

    // Per-surface copy and icon describe a list with nothing in it yet ("No cases yet"), which is a
    // lie over a filtered or failed one — so on those variants the generic copy wins and callers can
    // pass their own unconditionally. Only copy written for the error itself replaces it there, and a filtered
    // list's body names the search it was handed.
    const isEmptyVariant = variant === 'empty';
    const isErrorVariant = variant === 'error';
    const surfaceBodyId = isEmptyVariant ? bodyId : isErrorVariant ? errorBodyId : undefined;
    const resolvedTitleId = emptyStateTitleId(variant, titleId, errorTitleId);
    // `null` is an explicit "no body", so only `undefined` falls through to the generic key.
    // Trimmed, as the list does to decide the variant: spaces alone are no search to name.
    const query = searchText?.trim();
    const namesSearch = variant === 'filtered' && !!query;
    const resolvedBodyId = namesSearch
        ? 'empty-state.filtered.body-search'
        : surfaceBodyId === undefined
          ? GENERIC_BODY_ID[variant]
          : surfaceBodyId;
    const resolvedSurface = surface ?? resolvedTitleId;

    // Which lists are empty in practice is the point of the event, so it is keyed on the surface
    // and the variant rather than fired once per mount.
    useEffect(() => {
        posthog.capture('empty_state_shown', { surface: resolvedSurface, variant, size });
    }, [posthog, resolvedSurface, variant, size]);

    // The surface's own action belongs to the tenant-empty state ONLY. Over a filtered or errored
    // list the useful action is undoing the filter or retrying, and a "create" button there reads as
    // if the list were genuinely empty.
    const cta =
        variant === 'filtered' ? (
            onClearFilters ? (
                <Button {...buttonStyle({ variant: 'contained', color: 'secondary' })} onClick={onClearFilters}>
                    <FormattedMessage id="empty-state.action.clear-filters" />
                </Button>
            ) : null
        ) : isErrorVariant ? (
            onRetry ? (
                <Button {...buttonStyle({ variant: 'contained', color: 'secondary' })} onClick={onRetry}>
                    <RefreshIcon className="text-[18px]" />
                    <FormattedMessage id="empty-state.action.retry" />
                </Button>
            ) : null
        ) : (
            action
        );

    if (size === 'inline') {
        return (
            <div className={cn('flex flex-col items-center gap-2 px-3 py-3 text-center', className)}>
                <p className="text-fg-secondary text-meta m-0 leading-[1.5385]">
                    <FormattedMessage id={resolvedTitleId} values={titleValues} />
                </p>
                {cta}
            </div>
        );
    }

    const Icon = (isEmptyVariant ? icon : undefined) ?? DEFAULT_ICON[variant];
    const isPage = size === 'page';

    return (
        <div
            className={cn(
                'flex items-center justify-center',
                // A page-tier state fills the pane it was handed and centres vertically in it. The
                // top padding is NOT redundant with that centring: the content stack is ~245px, so
                // on a pane with little free space (`flex-1` inert, or a short detail tab) there is
                // almost nothing left to centre and the icon lands against the filter chrome. The
                // padding is the guaranteed clearance; centring only adds to it on a tall pane.
                // A panel-tier state is a block inside a card that must not stretch it.
                isPage ? 'flex-1 pt-20 pb-6' : 'py-10',
                className
            )}
        >
            <div className={cn('flex flex-col items-center text-center', isPage ? 'max-w-[480px]' : 'max-w-[320px]')}>
                <Icon className={cn('text-grey-400', isPage ? 'text-[48px]' : 'text-[32px]')} aria-hidden />
                <p
                    className={cn(
                        'text-fg new:text-balance m-0 font-semibold',
                        isPage ? 'mt-4 text-base leading-6' : 'text-body1 mt-3'
                    )}
                >
                    <FormattedMessage id={resolvedTitleId} values={titleValues} />
                </p>
                {resolvedBodyId ? (
                    <p
                        className={cn(
                            'text-fg-secondary m-0 text-pretty',
                            isPage ? 'text-body1 mt-1.5' : 'text-meta mt-1 leading-[1.5385]'
                        )}
                    >
                        <FormattedMessage id={resolvedBodyId} values={namesSearch ? { query } : bodyValues} />
                    </p>
                ) : null}
                {cta ? <div className={isPage ? 'mt-5' : 'mt-3.5'}>{cta}</div> : null}
                {isPage ? <EmptyStateLinks /> : null}
            </div>
        </div>
    );
};

export default EmptyState;
